import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, FileText, Briefcase, ArrowRight, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../api';

/**
 * Site-wide command palette / search modal.
 *
 * Triggered by:
 *  - parent passing `open` prop
 *  - Cmd/Ctrl + K
 *  - Esc closes
 *
 * Searches across published articles + case studies via /api/search.
 */
const PublicSearchModal = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ articles: [], case_studies: [] });
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Focus input on open + clear state on close
  useEffect(() => {
    if (open) {
      setQ('');
      setResults({ articles: [], case_studies: [] });
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Esc to close — listen on document with capture so we win over inputs
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.stopPropagation();
        e.preventDefault();
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setResults({ articles: [], case_studies: [] });
      return;
    }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.publicSearch(term);
        setResults(r);
      } catch {
        setResults({ articles: [], case_studies: [] });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [q, open]);

  const total = (results.articles?.length || 0) + (results.case_studies?.length || 0);

  const goTo = (href) => {
    onClose?.();
    setTimeout(() => navigate(href), 50);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-start justify-center pt-20 md:pt-28 px-4"
          onClick={onClose}
          data-testid="public-search-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[680px] bg-white rounded-2xl shadow-2xl border border-[#242326]/8 overflow-hidden"
            data-testid="public-search-modal"
          >
            {/* Input row */}
            <div className="flex items-center gap-3 px-4 md:px-5 py-4 border-b border-[#242326]/8">
              <Search className="w-4 h-4 text-[#B8B8B8] shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles, case studies, tags…"
                data-testid="public-search-input"
                className="flex-1 bg-transparent border-0 outline-none text-[#242326] text-base font-['Inter'] placeholder-[#B8B8B8]"
              />
              {loading ? (
                <Loader2 className="w-4 h-4 text-[#B8B8B8] animate-spin" />
              ) : q.trim() ? (
                <button
                  onClick={() => setQ('')}
                  data-testid="public-search-clear"
                  aria-label="Clear search"
                  className="text-[#B8B8B8] hover:text-[#242326]"
                >
                  <X className="w-4 h-4" />
                </button>
              ) : (
                <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#242326]/15 text-[#B8B8B8] text-[10px] font-mono">
                  Esc
                </kbd>
              )}
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto" data-testid="public-search-results">
              {q.trim().length < 2 ? (
                <div className="px-5 py-10 text-center" data-testid="public-search-empty-prompt">
                  <p className="text-[#B8B8B8] text-sm font-['Lato']">
                    Start typing to find articles and case studies.
                  </p>
                </div>
              ) : total === 0 && !loading ? (
                <div className="px-5 py-10 text-center" data-testid="public-search-no-results">
                  <p className="text-[#4A4A48] text-sm font-['Lato']">
                    No matches for &ldquo;{q}&rdquo;.
                  </p>
                </div>
              ) : (
                <>
                  {results.articles?.length > 0 && (
                    <div className="py-2">
                      <p className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-[#B8B8B8] font-['Inter']">
                        Articles
                      </p>
                      {results.articles.map((a) => (
                        <button
                          key={a.slug}
                          onClick={() => goTo(`/insights/${a.slug}`)}
                          data-testid={`public-search-article-${a.slug}`}
                          className="w-full text-left px-5 py-3 flex items-start gap-3 hover:bg-[#83BF42]/5 transition-colors group"
                        >
                          <FileText className="w-4 h-4 text-[#83BF42] mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[#242326] text-sm font-medium font-['Inter'] truncate group-hover:text-[#83BF42]">
                              {a.title}
                            </p>
                            <p className="text-[#B8B8B8] text-xs font-['Lato'] truncate">
                              {a.category} · {a.excerpt}
                            </p>
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-[#B8B8B8] mt-1 shrink-0 group-hover:text-[#83BF42]" />
                        </button>
                      ))}
                    </div>
                  )}

                  {results.case_studies?.length > 0 && (
                    <div className="py-2 border-t border-[#242326]/5">
                      <p className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-[#B8B8B8] font-['Inter']">
                        Case studies
                      </p>
                      {results.case_studies.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => goTo(`/work#${c.id}`)}
                          data-testid={`public-search-case-${c.id}`}
                          className="w-full text-left px-5 py-3 flex items-start gap-3 hover:bg-[#83BF42]/5 transition-colors group"
                        >
                          <Briefcase className="w-4 h-4 text-[#83BF42] mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[#242326] text-sm font-medium font-['Inter'] truncate group-hover:text-[#83BF42]">
                              {c.title}
                            </p>
                            <p className="text-[#B8B8B8] text-xs font-['Lato'] truncate">
                              {c.client}{c.industry ? ` · ${c.industry}` : ''}
                            </p>
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-[#B8B8B8] mt-1 shrink-0 group-hover:text-[#83BF42]" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer hint */}
            <div className="px-5 py-3 border-t border-[#242326]/8 bg-[#FDFEFA] flex items-center justify-between text-[11px] text-[#B8B8B8] font-['Inter']">
              <span className="flex items-center gap-1.5">
                <kbd className="px-1.5 py-0.5 rounded border border-[#242326]/15 font-mono">⌘K</kbd>
                <span>to open anywhere</span>
              </span>
              <span>{total > 0 ? `${total} result${total === 1 ? '' : 's'}` : ''}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PublicSearchModal;
